import React, {
  FunctionComponent,
  ComponentProps,
  useState,
  useMemo
} from 'react';
import { useEventCallback } from '../../hooks/useEventCallback';
import { StrictReactNode } from '../../types/StrictReactNode';
import { prepareProps } from '../../utils/propUtils';
import { ExpandableContainer } from '../ExpandableContainer/ExpandableContainer';
import { Icon, IconSize, IconType } from '../Icon/Icon';
import styles from './ExpandableContainerWithHeader.module.css';

export type ExpandableContainerWithHeaderProps = ComponentProps<'div'> & {
  renderHeader: (ctx: { expanded: boolean }) => StrictReactNode;
};

export const ExpandableContainerWithHeader: FunctionComponent<ExpandableContainerWithHeaderProps> =
  React.memo((props) => {
    const { children, renderHeader, ...remainingProps } = prepareProps({
      props
    });

    const [expanded, setExpanded] = useState(false);

    const renderedHeader = useMemo(() => {
      return renderHeader({ expanded });
    }, [renderHeader, expanded]);

    const handleHeaderClick = useEventCallback(() => {
      setExpanded((oldExpanded) => !oldExpanded);
    });

    return (
      <div {...remainingProps}>
        <div
          className={styles.header}
          onClick={handleHeaderClick}
          data-testid="ExpandableContainerWithHeader.Header"
        >
          {renderedHeader}
          <Icon
            name={expanded ? 'angle-up' : 'angle-down'}
            type={IconType.Solid}
            hasTextOnLeftSide={true}
            size={IconSize.Small}
          ></Icon>
        </div>
        <ExpandableContainer expanded={expanded}>
          <div className={styles.content}>{children}</div>
        </ExpandableContainer>
      </div>
    );
  });
